import { computeInvoiceBalance } from './invoiceBalance';
import { isInvoiceOwing } from './invoiceOwing';

// creditspread-b7e2c94a -- Seam 15: the shared client-side preview of an
// oldest-first credit spread. Lifted VERBATIM from RecordAccountPaymentModal's
// inline `preview` useMemo so a single copy feeds BOTH surfaces:
//   - Record Payment (Seam 11 Part C): previews a TYPED amount before creating
//     and spreading a new credit.
//   - Spread Credit (Seam 15): previews an EXISTING credit's remaining balance
//     before spreading it.
// Best-effort guidance only: the server recomputes each invoice's authoritative
// balance inside its own transaction at apply time (computeInvoiceBalance is the
// shared client mirror), so this shows what WILL happen, not a promise. The
// candidate set (OWING invoices with a positive authoritative balance —
// x2owing-6c1e9b37: Paid / Written Off / Void / Cancelled / Draft are skipped) and the
// oldest-first ordering mirror the server's spreadCreditAcrossInvoices exactly.

export const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// Oldest-first ordering key, mirroring the server's spreadCreditAcrossInvoices msOf.
export const msOf = (inv) => {
    const c = inv.createdAt;
    if (c && typeof c.toMillis === 'function') return c.toMillis();
    if (c && typeof c.toDate === 'function') { const d = c.toDate(); return d ? d.getTime() : 0; }
    const t = c ? new Date(c).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
};

// Greedily fill each open invoice oldest-first from `amount`. Returns
// { rows, leftover, totalApplied }. Callers guard their own preconditions (the
// Record Payment toggle, a credit's open/positive status); a non-positive amount
// here simply yields an empty spread.
export function computeSpreadPreview(invoices, amount) {
    const amountNum = round2(amount);
    if (!(amountNum > 0)) return { rows: [], leftover: 0, totalApplied: 0 };

    const candidates = (Array.isArray(invoices) ? invoices : [])
        .filter(isInvoiceOwing) // x2owing-6c1e9b37
        .map((inv) => ({ inv, balance: round2(computeInvoiceBalance(inv).balanceDue) }))
        .filter((x) => x.balance > 0.01);
    candidates.sort((a, b) => {
        const d = msOf(a.inv) - msOf(b.inv);
        if (d !== 0) return d;
        const an = a.inv.invoiceNumber, bn = b.inv.invoiceNumber;
        if (an != null && bn != null && String(an) !== String(bn)) return String(an) < String(bn) ? -1 : 1;
        return a.inv.id < b.inv.id ? -1 : 1;
    });

    let remaining = amountNum;
    const rows = [];
    for (const c of candidates) {
        if (remaining <= 0.01) break;
        const apply = round2(Math.min(remaining, c.balance));
        if (apply <= 0.01) continue;
        rows.push({
            id: c.inv.id,
            number: c.inv.invoiceNumber != null ? String(c.inv.invoiceNumber) : c.inv.id.slice(0, 6),
            balance: c.balance,
            apply,
            clears: apply >= c.balance - 0.001,
        });
        remaining = round2(remaining - apply);
    }
    return { rows, leftover: round2(remaining), totalApplied: round2(amountNum - remaining) };
}
